import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  DocumentSnapshot,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { RootState, AppDispatch } from "../../store";
import {
  Participation,
  RegistrationPhase,
  Tableau,
  Tour,
  Tournoi,
  tableauFromFirestoreData,
  mapTableauToFirestore,
} from "../../types";
import firestoreInstance from "../../services/firestoreInstance";
import { fetchParticipationsWithPlayers } from "../../helpers/fetchParticipationsWithPlayer";
import { DEFAULT_CANCELLATION_MESSAGE } from "../../constants/defaultCancellationMessage";
import { getConfiguredTournoiKey } from "../../utils/tournoiResolution";

/**
 * Dérive la RegistrationPhase depuis les anciens booléens pour assurer
 * la rétrocompatibilité avec les documents Firestore existants.
 */
function deriveRegistrationPhase(data: Partial<Tournoi>): RegistrationPhase {
  if (data.registrationPhase) return data.registrationPhase;
  if (data.registrationPhaseClosed) return "closedForPlayers";
  if (data.isRegisterClosed && data.isRegisterClosedAdmin) return "closed";
  if (data.isRegisterClosed && !data.isRegisterClosedAdmin) return "closedForPlayers";
  return "open";
}

export type TournoiState = {
  tournoi: Tournoi | null;
  boards: Tableau[];
  boardRegistrations: Tableau[];
  boardRegistrationsLoading: boolean;
  rounds: Tour[];
  allRegistrations: Participation[];
  allRegistrationsLoading: boolean;
  allRegistrationsError: string | null;
  allTournois: Tournoi[];
  allTournoisLoading: boolean;
};

const initialState: TournoiState = {
  tournoi: null,
  boards: [],
  boardRegistrations: [],
  boardRegistrationsLoading: false,
  rounds: [],
  allRegistrations: [],
  allRegistrationsLoading: false,
  allRegistrationsError: null,
  allTournois: [],
  allTournoisLoading: false,
};

type ExportedBoardData = Omit<
  Tableau,
  "id" | "date" | "isFull" | "matchs" | "tour" | "participation"
> & { date: string };

export type TournamentExportPayload = {
  formatVersion: 1;
  exportedAt: string;
  tournament: Omit<Tournoi, "id" | "tableaux">;
  boards: Array<{ id: string; data: ExportedBoardData }>;
};

type ImportRegistrationPhaseMode = "keepExported" | "resetToClosed";

type TournamentImportInput = {
  payload: TournamentExportPayload;
  keyOverride?: string;
  registrationPhaseMode?: ImportRegistrationPhaseMode;
};

const TOURNOI_IMPORT_DEFAULTS: Omit<Tournoi, "id"> = {
  label: "",
  key: "",
  current: false,
  isRegisterClosed: false,
  isRegisterClosedAdmin: false,
  registrationPhase: "closed",
  organizerClubIds: [],
  mail: "",
  adresse: "",
  lieuCourt: "",
  maps: "",
  mapsTitle: "",
  reglement: "",
  liveRefreshPeriodInSeconds: 10,
  statsCollectionName: "",
  dateOuvertureInscriptions: "",
  dateFinInscriptions: "",
  supplementApresDate: "",
  registrationPhaseClosed: false,
  alertThresholds: {
    cree: { "2": 60, "3": 20 },
    enCours: { "2": 50, "3": 100 },
  },
};

function removeUndefinedFields<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  );
}

function normalizeTournoiDoc(docSnap: DocumentSnapshot): Tournoi {
  const data = docSnap.data() as Tournoi;
  return {
    ...data,
    id: docSnap.id,
    registrationPhase: deriveRegistrationPhase(data),
    organizerClubIds: data.organizerClubIds ?? [],
  };
}

/**
 * Résolution du tournoi servi par ce build :
 * 1. REACT_APP_TOURNOI_KEY si défini
 * 2. sinon document avec current == true
 */
export const fetchTournoi = createAsyncThunk(
  "tournoi/fetchTournoi",
  async () => {
    const configuredKey = getConfiguredTournoiKey();
    if (configuredKey) {
      const byKey = await getDocs(
        query(
          collection(firestoreInstance, "tournois"),
          where("key", "==", configuredKey)
        )
      );
      if (byKey.empty) {
        throw new Error(
          `Tournoi avec la clé "${configuredKey}" introuvable (REACT_APP_TOURNOI_KEY).`
        );
      }
      return normalizeTournoiDoc(byKey.docs[0]);
    }

    const querySnapshot = await getDocs(
      query(
        collection(firestoreInstance, "tournois"),
        where("current", "==", true)
      )
    );
    const docSnap = querySnapshot.docs[0];
    if (!docSnap) {
      throw new Error("Aucun tournoi courant (current=true) trouvé.");
    }
    return normalizeTournoiDoc(docSnap);
  }
);

export const fetchTournoiByKey = createAsyncThunk<Tournoi, string>(
  "tournoi/fetchTournoiByKey",
  async (key) => {
    const snapshot = await getDocs(
      query(
        collection(firestoreInstance, "tournois"),
        where("key", "==", key)
      )
    );
    if (snapshot.empty) throw new Error(`Tournoi avec la clé "${key}" introuvable.`);
    const d = snapshot.docs[0];
    const data = d.data() as Tournoi;
    return {
      ...data,
      id: d.id,
      registrationPhase: deriveRegistrationPhase(data),
      organizerClubIds: data.organizerClubIds ?? [],
    };
  }
);

export const fetchAllTournois = createAsyncThunk<Tournoi[]>(
  "tournoi/fetchAllTournois",
  async () => {
    const snapshot = await getDocs(collection(firestoreInstance, "tournois"));
    return snapshot.docs.map((d) => {
      const data = d.data() as Tournoi;
      return {
        ...data,
        id: d.id,
        registrationPhase: deriveRegistrationPhase(data),
        organizerClubIds: data.organizerClubIds ?? [],
      };
    });
  }
);

const checkKeyUniqueness = async (key: string, excludeId?: string): Promise<void> => {
  const snapshot = await getDocs(
    query(collection(firestoreInstance, "tournois"), where("key", "==", key))
  );
  const conflict = snapshot.docs.find((d) => d.id !== excludeId);
  if (conflict) {
    throw new Error(`La clé "${key}" est déjà utilisée par le tournoi "${(conflict.data() as Tournoi).label}".`);
  }
};

export const createTournoi = createAsyncThunk<
  Tournoi,
  Omit<Tournoi, "id">,
  { dispatch: AppDispatch; rejectValue: string }
>("tournoi/createTournoi", async (data, { dispatch, rejectWithValue }) => {
  try {
    await checkKeyUniqueness(data.key);
    const docRef = await addDoc(collection(firestoreInstance, "tournois"), data);
    await dispatch(fetchAllTournois());
    return { ...data, id: docRef.id };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Erreur lors de la création.");
  }
});

export const updateTournoi = createAsyncThunk<
  void,
  { id: string; data: Partial<Omit<Tournoi, "id">> },
  { dispatch: AppDispatch; rejectValue: string }
>("tournoi/updateTournoi", async ({ id, data }, { dispatch, rejectWithValue }) => {
  try {
    if (data.key !== undefined) {
      await checkKeyUniqueness(data.key, id);
    }
    const tournamentRef = doc(firestoreInstance, "tournois", id);
    await updateDoc(tournamentRef, data);
    await dispatch(fetchAllTournois());
    await dispatch(fetchTournoi());
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Erreur lors de la mise à jour.");
  }
});

export const deleteTournoi = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch }
>("tournoi/deleteTournoi", async (id, { dispatch }) => {
  const tournamentRef = doc(firestoreInstance, "tournois", id);
  await deleteDoc(tournamentRef);
  await dispatch(fetchAllTournois());
});

export const setActiveTournoi = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch }
>("tournoi/setActiveTournoi", async (id, { dispatch }) => {
  const snapshot = await getDocs(collection(firestoreInstance, "tournois"));
  const batch = writeBatch(firestoreInstance);
  snapshot.docs.forEach((d) => {
    batch.update(d.ref, { current: d.id === id });
  });
  await batch.commit();
  await dispatch(fetchAllTournois());
  await dispatch(fetchTournoi());
});

export const exportTournamentWithBoards = createAsyncThunk<
  TournamentExportPayload,
  string
>("tournoi/exportTournamentWithBoards", async (tournamentId) => {
  const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);
  const tournamentSnap = await getDocs(
    query(collection(firestoreInstance, "tournois"), where("__name__", "==", tournamentId))
  );
  if (tournamentSnap.empty) {
    throw new Error("Tournoi introuvable.");
  }
  const tournamentData = tournamentSnap.docs[0].data() as Partial<Tournoi>;
  const registrationPhase = deriveRegistrationPhase(tournamentData);
  const baseTournament: Omit<Tournoi, "id" | "tableaux"> = {
    ...TOURNOI_IMPORT_DEFAULTS,
    ...tournamentData,
    registrationPhase,
    organizerClubIds: tournamentData.organizerClubIds ?? [],
  };
  const boardsSnap = await getDocs(collection(tournamentRef, "tableaux"));
  const boards = boardsSnap.docs.map((boardDoc) => {
    const board = tableauFromFirestoreData(
      boardDoc.id,
      boardDoc.data() as Record<string, unknown>
    );
    const exportedBoardData: ExportedBoardData = {
      name: board.name,
      label: board.label,
      heureLimite: board.heureLimite,
      nbPlace: board.nbPlace,
      realNbPlaces: board.realNbPlaces,
      date: board.date.toISOString(),
      sexe: board.sexe,
      cat: board.cat,
      handi: board.handi,
      classe: board.classe,
      min: board.min,
      max: board.max,
      obligatoire: board.obligatoire,
      ferme: board.ferme,
      hasWaitingQueue: board.hasWaitingQueue,
      fullPrice: board.fullPrice,
      comptePourLimiteJournaliere: board.comptePourLimiteJournaliere,
      inscriptionSurPlace: board.inscriptionSurPlace,
      offert: board.offert,
      incompatibleBoardIds: board.incompatibleBoardIds,
      tournoiId: board.tournoiId,
      // Export d'une structure réutilisable : on repart à zéro côté inscriptions.
      nbParticipants: 0,
    };
    return {
      id: boardDoc.id,
      data: exportedBoardData,
    };
  });
  return {
    formatVersion: 1,
    exportedAt: new Date().toISOString(),
    tournament: baseTournament,
    boards,
  };
});

export const importTournamentWithBoards = createAsyncThunk<
  void,
  TournamentImportInput,
  { dispatch: AppDispatch; rejectValue: string }
>("tournoi/importTournamentWithBoards", async (input, { dispatch, rejectWithValue }) => {
  try {
    const { payload, keyOverride, registrationPhaseMode = "keepExported" } = input;
    if (payload.formatVersion !== 1) {
      return rejectWithValue("Format d'export non supporté.");
    }
    if (!payload.tournament?.key || !payload.tournament?.label) {
      return rejectWithValue("Le fichier ne contient pas un tournoi valide.");
    }
    const targetKey = keyOverride?.trim() || payload.tournament.key;
    await checkKeyUniqueness(targetKey);

    const tournamentToCreate: Omit<Tournoi, "id"> = {
      ...TOURNOI_IMPORT_DEFAULTS,
      ...payload.tournament,
      key: targetKey,
      current: false,
      registrationPhase:
        registrationPhaseMode === "resetToClosed"
          ? "closed"
          : deriveRegistrationPhase(payload.tournament),
      organizerClubIds: payload.tournament.organizerClubIds ?? [],
    };
    const tournamentRef = await addDoc(
      collection(firestoreInstance, "tournois"),
      tournamentToCreate
    );

    try {
      const batch = writeBatch(firestoreInstance);
      payload.boards.forEach((board) => {
        if (!board.id || !board.data?.name) {
          return;
        }
        const date = new Date(board.data.date);
        if (Number.isNaN(date.getTime())) {
          return;
        }
        const boardToImport: Tableau = {
          id: board.id,
          name: board.data.name,
          label: board.data.label,
          heureLimite: board.data.heureLimite,
          nbPlace: board.data.nbPlace,
          realNbPlaces: board.data.realNbPlaces,
          date,
          sexe: board.data.sexe,
          cat: board.data.cat,
          handi: board.data.handi,
          classe: board.data.classe,
          min: board.data.min,
          max: board.data.max,
          obligatoire: board.data.obligatoire,
          ferme: board.data.ferme,
          hasWaitingQueue: board.data.hasWaitingQueue,
          fullPrice: board.data.fullPrice,
          comptePourLimiteJournaliere: board.data.comptePourLimiteJournaliere,
          inscriptionSurPlace: board.data.inscriptionSurPlace,
          offert: board.data.offert,
          incompatibleBoardIds: board.data.incompatibleBoardIds,
          tournoiId: tournamentRef.id,
          nbParticipants: 0,
        };
        const boardRef = doc(
          firestoreInstance,
          "tournois",
          tournamentRef.id,
          "tableaux",
          board.id
        );
        const boardFirestoreData = removeUndefinedFields({
          ...mapTableauToFirestore(boardToImport),
          tournoiId: tournamentRef.id,
        });
        batch.set(boardRef, boardFirestoreData);
      });
      await batch.commit();
    } catch (boardError) {
      await deleteDoc(doc(firestoreInstance, "tournois", tournamentRef.id));
      throw boardError;
    }
    await dispatch(fetchAllTournois());
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Erreur lors de l'import du tournoi."
    );
  }
});

export const fetchBoards = createAsyncThunk<
  Tableau[],
  void,
  { state: RootState }
>("tournoi/fetchBoards", async (_, thunkApi) => {
  const currentTournamentId = thunkApi.getState().tournoi.tournoi?.id;

  const snapshot = await getDocs(
    collection(firestoreInstance, "tournois", currentTournamentId!, "tableaux")
  );

  const tableaux = snapshot.docs.map((doc: DocumentSnapshot) =>
    tableauFromFirestoreData(doc.id, doc.data() as Record<string, unknown>)
  );

  const tabs = tableaux.sort((a, b) => {
    const dateA =
      a.date instanceof Date ? a.date.getTime() : new Date(a.date).getTime();
    const dateB =
      b.date instanceof Date ? b.date.getTime() : new Date(b.date).getTime();

    if (dateA !== dateB) {
      return dateA - dateB;
    }

    return a.name.localeCompare(b.name);
  });

  return tabs;
});

export const fetchRounds = createAsyncThunk<Tour[], void, { state: RootState }>(
  "tournoi/fetchRounds",
  async (_, thunkApi) => {
    const tours = (
      await getDocs(query(collection(firestoreInstance, "tour")))
    ).docs.map((doc) => ({ ...(doc.data() as Tour), ...{ id: doc.id } }));
    return tours;
  }
);

export const fetchBoardRegistrations = createAsyncThunk<
  Tableau[],
  { forceRefresh?: boolean } | void,
  { state: RootState }
>("tournoi/fetchBoardRegistrations", async (params, { getState }) => {
  const db = firestoreInstance;
  const tournamentId = getState().tournoi.tournoi?.id;
  if (!tournamentId) throw new Error("Tournoi courant introuvable.");

  // Charger les tableaux
  const tableauxSnap = await getDocs(
    collection(db, "tournois", tournamentId, "tableaux")
  );
  const tableauxMap = new Map(
    tableauxSnap.docs.map((d) => {
      const t = tableauFromFirestoreData(
        d.id,
        d.data() as Record<string, unknown>
      );
      return [d.id, { ...t, participation: [] as Participation[] }] as const;
    })
  );

  // Charger les participations enrichies avec rechargement forcé si demandé
  const forceRefresh =
    params && typeof params === "object" ? params.forceRefresh : false;
  const participations = await fetchParticipationsWithPlayers(
    tournamentId,
    forceRefresh
  );

  // Associer chaque participation à son tableau
  for (const part of participations) {
    const tableau = tableauxMap.get(part.tableauId);
    if (!tableau) continue;
    tableau.participation.push(part);
  }

  // Trier participations et tableaux
  const tableaux = Array.from(tableauxMap.values()).map(
    (t) =>
      ({
        ...t,
        participation: t.participation.sort(
          (a, b) => a.dateInscription.getTime() - b.dateInscription.getTime()
        ),
      } as Tableau)
  );

  return tableaux.sort((a, b) => a.name.localeCompare(b.name));
});

export const fetchAllRegistrations = createAsyncThunk<
  Participation[],
  { reload: boolean } | undefined,
  { state: RootState; rejectValue: string }
>("tournoi/fetchAllRegistrations", async (params, { getState, rejectWithValue }) => {
  try {
    const forceRefresh = params?.reload || false;
    const tournamentId = getState().tournoi.tournoi?.id;
    if (!tournamentId) {
      return rejectWithValue("Tournoi courant introuvable.");
    }

    const participations = await fetchParticipationsWithPlayers(
      tournamentId,
      forceRefresh
    );

    const sortedParticipations = [...participations].sort((a, b) => {
      const ta =
        a.dateInscription instanceof Date
          ? a.dateInscription.getTime()
          : 0;
      const tb =
        b.dateInscription instanceof Date
          ? b.dateInscription.getTime()
          : 0;
      return ta - tb;
    });

    return sortedParticipations;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : "Erreur lors du chargement des inscriptions."
    );
  }
});

/** Données pour créer un tableau (fiche + places initiales). */
export type BoardDefinitionCreateInput = {
  name: string;
  label: string;
  heureLimite: string;
  date: Date;
  nbPlace: number;
  realNbPlaces: number;
  sexe: string[];
  cat: string[];
  handi: string;
  classe?: string[];
  min?: number;
  max?: number;
  obligatoire?: boolean;
  fullPrice?: boolean;
  /** Si `false`, n’est pas décompté dans la limite de 2 tableaux / jour. Défaut : true. */
  comptePourLimiteJournaliere?: boolean;
  /**
   * Si `true`, info « inscription sur place » sur la page de saisie de licence.
   * Défaut : false.
   */
  inscriptionSurPlace?: boolean;
  /** Si `true`, exclu de la grille tarifaire (gratuit). Défaut : false. */
  offert?: boolean;
  /** Autres tableaux incompatibles (IDs) — synchronisation bidirectionnelle après création. */
  incompatibleBoardIds?: string[];
  /** Couleur de fond des blocs joueurs sur l'écran live (#RRGGBB). */
  liveColor?: string;
};

/** Mise à jour fiche sans places (pilotage sur l’autre page). */
export type BoardDefinitionUpdateInput = Omit<
  BoardDefinitionCreateInput,
  "nbPlace" | "realNbPlaces"
>;

export const updateNbPlaces = createAsyncThunk<
  void,
  { boardId: string; nbPlace: number },
  { state: RootState }
>(
  "tournoi/updateBoardNbPlaces",
  async ({ boardId, nbPlace }, { dispatch, getState }) => {
    const currentTournamentId: string = getState().tournoi.tournoi?.id;
    const tournamentRef = doc(
      firestoreInstance,
      "tournois",
      currentTournamentId,
      "tableaux",
      boardId
    );

    await updateDoc(tournamentRef, { nbPlace });
    await dispatch(fetchBoardRegistrations());
    return;
  }
);

/**
 * Met à jour les incompatibilités pour un tableau et reflète la symétrie :
 * si A est incompatible avec B, B liste aussi A.
 */
export const syncBoardIncompatibilities = createAsyncThunk<
  void,
  { boardId: string; incompatibleBoardIds: string[] },
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  "tournoi/syncBoardIncompatibilities",
  async ({ boardId, incompatibleBoardIds }, { dispatch, getState, rejectWithValue }) => {
    try {
      const tournamentId = getState().tournoi.tournoi?.id;
      if (!tournamentId) {
        return rejectWithValue("Tournoi courant introuvable.");
      }
      const boards = getState().tournoi.boards;
      const self = boards.find((b) => b.id === boardId);
      if (!self) {
        return rejectWithValue("Tableau introuvable.");
      }
      const knownIds = new Set(
        boards.map((b) => b.id).filter((id): id is string => Boolean(id))
      );
      const newSet = Array.from(
        new Set(
          incompatibleBoardIds.filter(
            (id) => Boolean(id) && id !== boardId && knownIds.has(id)
          )
        )
      ).sort();
      const oldSet = [...(self.incompatibleBoardIds ?? [])].sort();
      const removed = oldSet.filter((id) => !newSet.includes(id));
      const added = newSet.filter((id) => !oldSet.includes(id));

      const boardRef = (id: string) =>
        doc(firestoreInstance, "tournois", tournamentId, "tableaux", id);

      const batch = writeBatch(firestoreInstance);
      batch.update(boardRef(boardId), { incompatibleBoardIds: newSet });

      for (const r of removed) {
        const other = boards.find((b) => b.id === r);
        if (!other?.id) continue;
        const next = (other.incompatibleBoardIds ?? []).filter(
          (id) => id !== boardId
        );
        batch.update(boardRef(r), { incompatibleBoardIds: next });
      }
      for (const a of added) {
        const other = boards.find((b) => b.id === a);
        if (!other?.id) continue;
        const next = Array.from(
          new Set([...(other.incompatibleBoardIds ?? []), boardId])
        );
        batch.update(boardRef(a), { incompatibleBoardIds: next });
      }

      await batch.commit();
      await dispatch(fetchBoards());
    } catch (e) {
      return rejectWithValue(
        e instanceof Error
          ? e.message
          : "Erreur lors de la synchronisation des incompatibilités."
      );
    }
  }
);

export const createBoard = createAsyncThunk<
  string,
  BoardDefinitionCreateInput,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>("tournoi/createBoard", async (input, { dispatch, getState, rejectWithValue }) => {
  try {
    const tournamentId = getState().tournoi.tournoi?.id;
    if (!tournamentId) {
      return rejectWithValue("Tournoi courant introuvable.");
    }
    const newTableau: Tableau = {
      name: input.name.trim(),
      label: input.label.trim(),
      heureLimite: input.heureLimite.trim(),
      nbPlace: input.nbPlace,
      realNbPlaces: input.realNbPlaces,
      date: input.date,
      sexe: input.sexe,
      cat: input.cat,
      handi: input.handi,
      classe: input.classe?.length ? input.classe : undefined,
      min: input.min,
      max: input.max,
      obligatoire: input.obligatoire ?? false,
      fullPrice: input.fullPrice ?? false,
      comptePourLimiteJournaliere: input.comptePourLimiteJournaliere !== false,
      inscriptionSurPlace: input.inscriptionSurPlace === true,
      offert: input.offert === true,
      ferme: false,
      hasWaitingQueue: true,
      nbParticipants: 0,
      waitlistCount: 0,
      tournoiId: tournamentId,
      ...(input.liveColor ? { liveColor: input.liveColor } : {}),
    };
    const payload = mapTableauToFirestore(newTableau);
    const firestorePayload: Record<string, unknown> = {
      ...payload,
      nbParticipants: 0,
      waitlistCount: 0,
    };
    Object.keys(firestorePayload).forEach((k) => {
      if (firestorePayload[k] === undefined) {
        delete firestorePayload[k];
      }
    });
    const colRef = collection(
      firestoreInstance,
      "tournois",
      tournamentId,
      "tableaux"
    );
    const docRef = await addDoc(colRef, firestorePayload);
    await dispatch(fetchBoards());
    const incompatible = (input.incompatibleBoardIds ?? []).filter(
      (id) => id && id !== docRef.id
    );
    await dispatch(
      syncBoardIncompatibilities({
        boardId: docRef.id,
        incompatibleBoardIds: incompatible,
      })
    ).unwrap();
    await dispatch(fetchBoardRegistrations());
    return docRef.id;
  } catch (e) {
    return rejectWithValue(
      e instanceof Error ? e.message : "Erreur lors de la création du tableau."
    );
  }
});

export const updateBoardDefinition = createAsyncThunk<
  void,
  { boardId: string; data: BoardDefinitionUpdateInput },
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  "tournoi/updateBoardDefinition",
  async ({ boardId, data }, { dispatch, getState, rejectWithValue }) => {
    try {
      const tournamentId = getState().tournoi.tournoi?.id;
      if (!tournamentId) {
        return rejectWithValue("Tournoi courant introuvable.");
      }
      const tournamentRef = doc(
        firestoreInstance,
        "tournois",
        tournamentId,
        "tableaux",
        boardId
      );
      await updateDoc(tournamentRef, {
        name: data.name.trim(),
        label: data.label.trim(),
        heureLimite: data.heureLimite.trim(),
        date: Timestamp.fromDate(data.date),
        sexe: data.sexe,
        cat: data.cat,
        handi: data.handi,
        classe: data.classe ?? [],
        obligatoire: data.obligatoire ?? false,
        fullPrice: data.fullPrice ?? false,
        comptePourLimiteJournaliere: data.comptePourLimiteJournaliere !== false,
        inscriptionSurPlace: data.inscriptionSurPlace === true,
        offert: data.offert === true,
        min: data.min ?? null,
        max: data.max ?? null,
        liveColor: data.liveColor?.trim() || null,
      });
      await dispatch(fetchBoards());
      await dispatch(fetchBoardRegistrations());
    } catch (e) {
      return rejectWithValue(
        e instanceof Error ? e.message : "Erreur lors de la mise à jour."
      );
    }
  }
);

export const deleteBoard = createAsyncThunk<
  void,
  { boardId: string },
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>("tournoi/deleteBoard", async ({ boardId }, { dispatch, getState, rejectWithValue }) => {
  try {
    const tournamentId = getState().tournoi.tournoi?.id;
    if (!tournamentId) {
      return rejectWithValue("Tournoi courant introuvable.");
    }
    const boards = getState().tournoi.boards;
    const boardRef = doc(
      firestoreInstance,
      "tournois",
      tournamentId,
      "tableaux",
      boardId
    );
    const refPath = (id: string) =>
      doc(firestoreInstance, "tournois", tournamentId, "tableaux", id);
    const batch = writeBatch(firestoreInstance);
    for (const b of boards) {
      if (b.id === boardId) continue;
      const inc = b.incompatibleBoardIds ?? [];
      if (inc.includes(boardId)) {
        batch.update(refPath(b.id as string), {
          incompatibleBoardIds: inc.filter((id) => id !== boardId),
        });
      }
    }
    batch.delete(boardRef);
    await batch.commit();
    await dispatch(fetchBoards());
    await dispatch(fetchBoardRegistrations());
  } catch (e) {
    return rejectWithValue(
      e instanceof Error ? e.message : "Erreur lors de la suppression."
    );
  }
});

export const updateRealNbPlaces = createAsyncThunk<
  void,
  { boardId: string; realNbPlaces: number },
  { state: RootState }
>(
  "tournoi/updateRealNbPlaces",
  async ({ boardId, realNbPlaces }, { dispatch, getState }) => {
    const currentTournamentId: string = getState().tournoi.tournoi?.id;
    const tournamentRef = doc(
      firestoreInstance,
      "tournois",
      currentTournamentId,
      "tableaux",
      boardId
    );

    await updateDoc(tournamentRef, { realNbPlaces });
    await dispatch(fetchBoardRegistrations());
    return;
  }
);

export const changeBoardStatus = createAsyncThunk<
  void,
  { boardId: string; closed: boolean },
  { state: RootState }
>(
  "tournoi/changeBoardStatus",
  async ({ boardId, closed }, { dispatch, getState }) => {
    const currentTournamentId: string = getState().tournoi.tournoi?.id;
    const tournamentRef = doc(
      firestoreInstance,
      "tournois",
      currentTournamentId,
      "tableaux",
      boardId
    );

    await updateDoc(tournamentRef, { ferme: closed });
    await dispatch(fetchBoardRegistrations());
    return;
  }
);

export const changeBoardQueueStatus = createAsyncThunk<
  void,
  { boardId: string; hasWaitingQueue: boolean },
  { state: RootState }
>(
  "tournoi/changeBoardQueueStatus",
  async ({ boardId, hasWaitingQueue }, { dispatch, getState }) => {
    const currentTournamentId: string = getState().tournoi.tournoi?.id;
    const tournamentRef = doc(
      firestoreInstance,
      "tournois",
      currentTournamentId,
      "tableaux",
      boardId
    );

    await updateDoc(tournamentRef, { hasWaitingQueue });
    await dispatch(fetchBoardRegistrations());
    return;
  }
);

export const changeRegistrationPhase = createAsyncThunk<
  void,
  { tournamentId: string; phase: RegistrationPhase },
  { dispatch: AppDispatch }
>(
  "tournoi/changeRegistrationPhase",
  async ({ tournamentId, phase }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);
    await updateDoc(tournamentRef, { registrationPhase: phase });
    await dispatch(fetchTournoi());
  }
);

export const changeCancellationMessage = createAsyncThunk<
  void,
  { tournamentId: string; cancellationMessage: string },
  { dispatch: AppDispatch }
>(
  "tournoi/changeCancellationMessage",
  async ({ tournamentId, cancellationMessage }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);
    await updateDoc(tournamentRef, { cancellationMessage });
    await dispatch(fetchTournoi());
  }
);

export const cancelTournament = createAsyncThunk<
  void,
  { tournamentId: string; cancellationMessage?: string },
  { dispatch: AppDispatch; state: RootState }
>(
  "tournoi/cancelTournament",
  async ({ tournamentId, cancellationMessage }, { dispatch, getState }) => {
    const currentMessage =
      cancellationMessage?.trim() ||
      getState().tournoi.tournoi?.cancellationMessage?.trim() ||
      DEFAULT_CANCELLATION_MESSAGE;
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);
    await updateDoc(tournamentRef, {
      registrationPhase: "cancelled",
      cancellationMessage: currentMessage,
    });
    await dispatch(fetchTournoi());
  }
);

export const restoreCancelledTournament = createAsyncThunk<
  void,
  { tournamentId: string; phase?: RegistrationPhase },
  { dispatch: AppDispatch }
>(
  "tournoi/restoreCancelledTournament",
  async ({ tournamentId, phase = "closedForPlayers" }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);
    await updateDoc(tournamentRef, { registrationPhase: phase });
    await dispatch(fetchTournoi());
  }
);

export const changeParticipationPaymentStatus = createAsyncThunk<
  any,
  { participationId: string; status: string },
  { state: RootState }
>(
  "player/changeParticipationPaymentStatus",
  async ({ participationId, status }, { getState, dispatch }) => {
    const tournamentId = getState().tournoi.tournoi?.id;
    if (!tournamentId) throw new Error("Tournoi courant introuvable.");
    // le champ paymentStatus est dans le document d'id "data" de la sous-collection "private" de la collection participation
    await updateDoc(
      doc(
        firestoreInstance,
        "tournois",
        tournamentId,
        "participation",
        participationId,
        "private",
        "data"
      ),
      { paymentStatus: status }
    );

    await dispatch(fetchAllRegistrations({ reload: true }));
    return;
  }
);

export const changeTournamentStatsCollection = createAsyncThunk<
  void,
  { tournamentId: string; statsCollectionName: string },
  { dispatch: AppDispatch }
>(
  "tournoi/changeTournamentStatsCollection",
  async ({ tournamentId, statsCollectionName }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);
    await updateDoc(tournamentRef, { statsCollectionName });
    await dispatch(fetchTournoi());
    return;
  }
);

export const changeTournamentAlertThresholds = createAsyncThunk<
  void,
  { tournamentId: string; alertThresholds: Tournoi["alertThresholds"] },
  { dispatch: AppDispatch }
>(
  "tournoi/changeTournamentAlertThresholds",
  async ({ tournamentId, alertThresholds }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);
    await updateDoc(tournamentRef, { alertThresholds });
    await dispatch(fetchTournoi());
    return;
  }
);

// 🔥 Gestion des dossards (sous-collection tournois/{id}/dossards/{playerId})

// Fonction de tri pour les dossards : points décroissants, puis nom/prénom alphabétique
const sortPlayersForDossards = (
  a: { points?: string; name?: string; surname?: string },
  b: { points?: string; name?: string; surname?: string }
) => {
  // 1. Tri par points décroissants
  const pointsA = parseInt(a.points ?? "", 10) || 0;
  const pointsB = parseInt(b.points ?? "", 10) || 0;

  if (pointsA !== pointsB) {
    return pointsB - pointsA; // Décroissant
  }

  // 2. Si points égaux, tri par nom alphabétique
  const nameA = a.name?.toLowerCase() || "";
  const nameB = b.name?.toLowerCase() || "";

  if (nameA !== nameB) {
    return nameA.localeCompare(nameB);
  }

  // 3. Si noms égaux, tri par prénom alphabétique
  const surnameA = a.surname?.toLowerCase() || "";
  const surnameB = b.surname?.toLowerCase() || "";

  return surnameA.localeCompare(surnameB);
};

const FIRESTORE_BATCH_LIMIT = 500;

function collectUniquePlayersFromParticipations(
  participations: Participation[]
) {
  const uniquePlayers = new Map<string, NonNullable<Participation["players"]>>();
  participations.forEach((p) => {
    if (p.playersId && p.players && p.players.id) {
      if (!uniquePlayers.has(p.playersId)) {
        uniquePlayers.set(p.playersId, p.players);
      }
    }
  });
  return uniquePlayers;
}

export const generateDossards = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  "tournoi/generateDossards",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const tournamentId = getState().tournoi.tournoi?.id;
      if (!tournamentId) throw new Error("Tournoi courant introuvable.");
      const participations = await fetchParticipationsWithPlayers(
        tournamentId,
        true
      );

      if (!participations || participations.length === 0) {
        throw new Error("Aucune inscription trouvée dans le tournoi");
      }

      const uniquePlayers = collectUniquePlayersFromParticipations(
        participations
      );

      if (uniquePlayers.size === 0) {
        throw new Error("Aucun joueur valide trouvé dans les inscriptions");
      }

      const sortedPlayers = Array.from(uniquePlayers.values())
        .filter((player) => player && player.name && player.surname)
        .sort(sortPlayersForDossards);

      if (sortedPlayers.length === 0) {
        throw new Error("Aucun joueur avec des données complètes trouvé");
      }

      let nextDossard = 1;
      for (const player of sortedPlayers) {
        if (
          player.dossard &&
          typeof player.dossard === "number" &&
          player.dossard >= nextDossard
        ) {
          nextDossard = player.dossard + 1;
        }
      }

      const playersWithoutDossard = sortedPlayers.filter(
        (p) => !p.dossard || typeof p.dossard !== "number"
      );
      if (playersWithoutDossard.length === 0) {
        throw new Error("Tous les joueurs ont déjà un dossard");
      }

      for (let i = 0; i < playersWithoutDossard.length; i += FIRESTORE_BATCH_LIMIT) {
        const chunk = playersWithoutDossard.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const batch = writeBatch(firestoreInstance);
        for (const player of chunk) {
          const dossardRef = doc(
            firestoreInstance,
            "tournois",
            tournamentId,
            "dossards",
            player.id as string
          );
          batch.set(dossardRef, {
            dossard: nextDossard++,
            updatedAt: Timestamp.now(),
          });
        }
        await batch.commit();
      }

      await dispatch(fetchAllRegistrations({ reload: true }));
      await dispatch(fetchBoardRegistrations({ forceRefresh: true }));
    } catch (error) {
      console.error("Erreur dans generateDossards:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Erreur lors de la génération des dossards"
      );
    }
  }
);

export const regenerateAllDossards = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  "tournoi/regenerateAllDossards",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const tournamentId = getState().tournoi.tournoi?.id;
      if (!tournamentId) throw new Error("Tournoi courant introuvable.");
      const participations = await fetchParticipationsWithPlayers(
        tournamentId,
        true
      );

      if (!participations || participations.length === 0) {
        throw new Error("Aucune inscription trouvée dans le tournoi");
      }

      const uniquePlayers = collectUniquePlayersFromParticipations(
        participations
      );

      if (uniquePlayers.size === 0) {
        throw new Error("Aucun joueur valide trouvé dans les inscriptions");
      }

      const sortedPlayers = Array.from(uniquePlayers.values())
        .filter((player) => player && player.name && player.surname)
        .sort(sortPlayersForDossards);

      if (sortedPlayers.length === 0) {
        throw new Error("Aucun joueur avec des données complètes trouvé");
      }

      let dossard = 1;
      for (let i = 0; i < sortedPlayers.length; i += FIRESTORE_BATCH_LIMIT) {
        const chunk = sortedPlayers.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const batch = writeBatch(firestoreInstance);
        for (const player of chunk) {
          const dossardRef = doc(
            firestoreInstance,
            "tournois",
            tournamentId,
            "dossards",
            player.id as string
          );
          batch.set(dossardRef, {
            dossard: dossard++,
            updatedAt: Timestamp.now(),
          });
        }
        await batch.commit();
      }

      await dispatch(fetchAllRegistrations({ reload: true }));
      await dispatch(fetchBoardRegistrations({ forceRefresh: true }));
    } catch (error) {
      console.error("Erreur dans regenerateAllDossards:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Erreur lors de la régénération des dossards"
      );
    }
  }
);

export const clearAllDossards = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  "tournoi/clearAllDossards",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const tournamentId = getState().tournoi.tournoi?.id;
      if (!tournamentId) throw new Error("Tournoi courant introuvable.");

      const dossardsSnap = await getDocs(
        collection(firestoreInstance, "tournois", tournamentId, "dossards")
      );

      if (dossardsSnap.empty) {
        throw new Error("Aucun dossard à supprimer");
      }

      const docs = dossardsSnap.docs;
      for (let i = 0; i < docs.length; i += FIRESTORE_BATCH_LIMIT) {
        const chunk = docs.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const batch = writeBatch(firestoreInstance);
        for (const d of chunk) {
          batch.delete(d.ref);
        }
        await batch.commit();
      }

      await dispatch(fetchAllRegistrations({ reload: true }));
      await dispatch(fetchBoardRegistrations({ forceRefresh: true }));
    } catch (error) {
      console.error("Erreur dans clearAllDossards:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Erreur lors de la suppression des dossards"
      );
    }
  }
);

// Nouvelle action pour générer un fichier CSV des joueurs inscrits
export const generatePlayersCSV = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  "tournoi/generatePlayersCSV",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const tournamentId = getState().tournoi.tournoi?.id;
      if (!tournamentId) throw new Error("Tournoi courant introuvable.");
      const participations = await fetchParticipationsWithPlayers(tournamentId);

      if (!participations || participations.length === 0) {
        throw new Error("Aucune inscription trouvée dans le tournoi");
      }

      // Récupérer tous les joueurs uniques qui ont au moins une inscription
      const uniquePlayers = new Map();
      participations.forEach((p) => {
        // Vérifier que le joueur existe et a un ID valide
        if (p.playersId && p.players && p.players.id) {
          if (!uniquePlayers.has(p.playersId)) {
            uniquePlayers.set(p.playersId, p.players);
          }
        }
      });

      if (uniquePlayers.size === 0) {
        throw new Error("Aucun joueur valide trouvé dans les inscriptions");
      }

      // Vérifier que tous les joueurs ont un dossard
      const playersWithDossard = Array.from(uniquePlayers.values()).filter(
        (p) => p && p.dossard && typeof p.dossard === "number"
      );

      if (playersWithDossard.length !== uniquePlayers.size) {
        throw new Error(
          "Tous les joueurs doivent avoir un dossard pour générer le CSV"
        );
      }

      // Trier les joueurs par dossard
      const sortedPlayers = playersWithDossard.sort(
        (a, b) => a.dossard - b.dossard
      );

      // Créer le contenu CSV
      const csvHeader = "Dossard,Numéro de licence,Nom,Prénom\n";
      const csvContent = sortedPlayers
        .map((player) => {
          const nom = player.name || "";
          const prenom = player.surname || "";
          const licenceId = player.licenceId || "";
          return `${player.dossard},"${licenceId}","${nom}","${prenom}"`;
        })
        .join("\n");

      const csvData = csvHeader + csvContent;

      // Créer et télécharger le fichier
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "joueurs_inscrits.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erreur dans generatePlayersCSV:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Erreur lors de la génération du fichier CSV"
      );
    }
  }
);


const tournoiSlice = createSlice({
  name: "tournoi",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchTournoi.fulfilled,
        (state: TournoiState, action: PayloadAction<Tournoi>) => {
          state.tournoi = action.payload;
        }
      )
      .addCase(
        fetchTournoiByKey.fulfilled,
        (state: TournoiState, action: PayloadAction<Tournoi>) => {
          state.tournoi = action.payload;
        }
      )
      .addCase(fetchAllTournois.pending, (state: TournoiState) => {
        state.allTournoisLoading = true;
      })
      .addCase(
        fetchAllTournois.fulfilled,
        (state: TournoiState, action: PayloadAction<Tournoi[]>) => {
          state.allTournois = action.payload;
          state.allTournoisLoading = false;
        }
      )
      .addCase(fetchAllTournois.rejected, (state: TournoiState) => {
        state.allTournoisLoading = false;
      })
      .addCase(
        fetchBoards.fulfilled,
        (state: TournoiState, action: PayloadAction<Tableau[]>) => {
          state.boards = action.payload;
          //.sort((a, b) =>
          //   a.name.localeCompare(b.name)
          // );
        }
      )
      .addCase(
        fetchBoardRegistrations.fulfilled,
        (state: TournoiState, action: PayloadAction<Tableau[]>) => {
          state.boardRegistrations = action.payload.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          state.boardRegistrationsLoading = false;
        }
      )
      .addCase(fetchBoardRegistrations.pending, (state: TournoiState) => {
        state.boardRegistrationsLoading = true;
      })
      .addCase(
        fetchRounds.fulfilled,
        (state: TournoiState, action: PayloadAction<Tour[]>) => {
          state.rounds = action.payload.sort((a, b) => a.ordre - b.ordre);
        }
      )
      .addCase(
        fetchAllRegistrations.fulfilled,
        (state: TournoiState, action: PayloadAction<Participation[]>) => {
          const boardsMap = new Map(state.boards.map((b) => [b.id, b]));

          state.allRegistrations = action.payload
            .map((p) => {
              const tableau = boardsMap.get(p.tableauId);
              // Si pas de tableau dans le state, on garde quand même la participation
              // Le tableau sera chargé plus tard ou on utilisera les données de base
              return { ...p, tableau: tableau || p.tableau };
            })
            .filter((p): p is Participation => p !== null);

          state.allRegistrationsLoading = false;
          state.allRegistrationsError = null;
        }
      )
      .addCase(fetchAllRegistrations.pending, (state: TournoiState, action) => {
        const { meta } = action;
        state.allRegistrationsLoading = true;
        state.allRegistrationsError = null;
        if (meta && meta.arg && meta.arg.reload) {
          // Garde la liste affichée pendant un rafraîchissement
          return;
        }
        state.allRegistrations = [];
      })
      .addCase(fetchAllRegistrations.rejected, (state: TournoiState, action) => {
        state.allRegistrationsLoading = false;
        state.allRegistrationsError =
          action.payload ||
          action.error.message ||
          "Erreur lors du chargement des inscriptions.";
        console.error(
          "[fetchAllRegistrations] échec:",
          state.allRegistrationsError,
          action.error
        );
      })
      .addCase(
        changeTournamentStatsCollection.fulfilled,
        (state: TournoiState, action: PayloadAction<void>) => {
          // Le state est déjà mis à jour par fetchTournoi
        }
      )
      // Gestion des erreurs pour les dossards
      .addCase(generateDossards.rejected, (state: TournoiState, action) => {
        console.error(
          "Erreur lors de la génération des dossards:",
          action.payload
        );
      })
      .addCase(
        regenerateAllDossards.rejected,
        (state: TournoiState, action) => {
          console.error(
            "Erreur lors de la régénération des dossards:",
            action.payload
          );
        }
      )
      .addCase(clearAllDossards.rejected, (state: TournoiState, action) => {
        console.error(
          "Erreur lors de la suppression des dossards:",
          action.payload
        );
      });
  },
});

export default tournoiSlice.reducer;

export const getTournoi = (state: RootState): Tournoi => state.tournoi.tournoi!;

export const getRegistrationPhase = (state: RootState): RegistrationPhase =>
  state.tournoi.tournoi?.registrationPhase ?? "closed";

export const getOrganizerClubIds = (state: RootState): string[] =>
  state.tournoi.tournoi?.organizerClubIds ?? [];

/**
 * Indique si les inscriptions sont accessibles pour l'utilisateur courant.
 * Garde de page : ne vérifie PAS l'éligibilité club (vérifiée post-fetch dans Register.tsx).
 */
export const getAreOpenedRegistrations = (state: RootState): boolean => {
  const isAdmin = state.session.admin;
  const phase = state.tournoi.tournoi?.registrationPhase ?? "closed";
  if (phase === "cancelled") return false;
  if (isAdmin) return phase !== "closed";
  return phase === "open";
};

/**
 * Vérifie si le joueur actuellement chargé est éligible à la phase courante.
 * Utilisé après fetchPlayerFromLicence dans Register.tsx.
 * - En phase open / closedForPlayers : toujours true (ou géré par getAreOpenedRegistrations)
 * - En phase preRegistration : vérifie que le clubId du joueur est dans organizerClubIds
 */
export const getIsEligibleForCurrentPhase = (state: RootState): boolean => {
  const phase = state.tournoi.tournoi?.registrationPhase ?? "closed";
  if (phase !== "preRegistration") return true;
  const clubId = (state as any).player?.player?.clubId as string | undefined;
  const organizerClubIds = state.tournoi.tournoi?.organizerClubIds ?? [];
  return !!clubId && organizerClubIds.includes(clubId);
};

/**
 * Indique si la page de pré-inscription est accessible pour l'utilisateur courant.
 * - admin : autorisé tant que la phase n'est pas "closed"
 * - non-admin : autorisé uniquement en phase "preRegistration"
 */
export const getAreOpenedPreRegistrations = (state: RootState): boolean => {
  const isAdmin = state.session.admin;
  const phase = state.tournoi.tournoi?.registrationPhase ?? "closed";
  if (phase === "cancelled") return false;
  if (isAdmin) return phase !== "closed";
  return phase === "preRegistration";
};

export const getBoards = (state: RootState) => state.tournoi.boards;

export const getBoardsSortedByName = (state: RootState) => {
  return [...state.tournoi.boards].sort((a, b) => a.name.localeCompare(b.name));
};

export const getRounds = (state: RootState) => state.tournoi.rounds;

export const getBoardRegistrations = (state: RootState) =>
  state.tournoi.boardRegistrations;

export const getBoardRegistrationsLoading = (state: RootState) =>
  state.tournoi.boardRegistrationsLoading;

export const getAllRegistrations = (state: RootState) =>
  state.tournoi.allRegistrations;

export const getAllRegistrationsLoading = (state: RootState) =>
  state.tournoi.allRegistrationsLoading;

export const getAllRegistrationsError = (state: RootState) =>
  state.tournoi.allRegistrationsError;

export const getLiveRefreshPeriodInSeconds = (state: RootState) =>
  state.tournoi.tournoi?.liveRefreshPeriodInSeconds
    ? state.tournoi.tournoi?.liveRefreshPeriodInSeconds * 1000
    : 10000;

// Nouveau sélecteur pour vérifier si tous les joueurs ont un dossard
export const getAreAllDossardsGenerated = (state: RootState) => {
  const participations = state.tournoi.allRegistrations;

  if (participations.length === 0) {
    return false;
  }

  const uniquePlayers = new Map();
  participations.forEach((p) => {
    if (p.playersId && p.players && p.players.id) {
      if (!uniquePlayers.has(p.playersId)) {
        uniquePlayers.set(p.playersId, p.players);
      }
    }
  });

  return Array.from(uniquePlayers.values()).every(
    (p) => p && p.dossard && typeof p.dossard === "number"
  );
};

export const getIsRegistrationPhaseClosed = (state: RootState): boolean => {
  const phase = state.tournoi.tournoi?.registrationPhase ?? "closed";
  return phase === "closedForPlayers" || phase === "cancelled";
};

export const getIsTournamentCancelled = (state: RootState): boolean =>
  (state.tournoi.tournoi?.registrationPhase ?? "closed") === "cancelled";

export const getAllTournoisList = (state: RootState): Tournoi[] =>
  state.tournoi.allTournois;

export const getAllTournoisLoading = (state: RootState): boolean =>
  state.tournoi.allTournoisLoading;
